import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import './DesignStatement.css'



// ── Content ─────────────────────────────────────────────────────────────────

const MANIFESTO_CHUNKS = [
    { text: 'Every frame, a new answer to a specific question.', em: false },
    { text: ' Not imported.', em: true },
    { text: ' Not approximated.', em: true },
    { text: ' Engineered.', em: true },
]

const PRINCIPLES = [
    {
        num: '01 — Geometry',
        title: 'The mission writes\nthe airframe.',
        body: 'Spray pattern, payload weight, terrain profile, wind load — every variable shapes a geometry that a catalogue drone will never accommodate. We design from the requirement outward, not from a stock frame inward.',
    },
    {
        num: '02 — Origin',
        title: 'Indian skies deserve\nIndian engineering.',
        body: 'Every structural member, arm, and composite shell is conceived, calculated, and built in-house. Not because imports are unavailable — but because sovereignty in aerospace begins with the decisions no one else sees.',
    },
    {
        num: '03 — Integrity',
        title: 'No off-the-shelf\ncompromises.',
        body: 'When a commercially available component cannot meet the load case, we machine it ourselves. Accepting a lesser spec is not a compromise — it is a failure mode we choose to design out entirely.',
    },
]

const MARQUEE_TAGS = [
    'CUSTOM GEOMETRY',
    'AGRICULTURE FRAMES',
    'SURVEY CLASS',
    'HEAVY LIFT',
    'IN-HOUSE COMPOSITES',
    'INDIGENOUS MANUFACTURE',
    'PRECISION ENGINEERING',
    'MISSION-FIRST DESIGN',
    'ZERO COMPROMISE',
    'MADE IN INDIA',
]

// ── Word splitter ────────────────────────────────────────────────────────────

function buildManifestoWords() {
    const words = []

    MANIFESTO_CHUNKS.forEach(chunk => {
        const parts = chunk.text.split(/(\s+)/)

        parts.forEach(part => {
            words.push({ word: part, em: chunk.em })
        })
    })

    return words
}
// ── Component ────────────────────────────────────────────────────────────────

export default function DesignStatement() {
    const wrapRef = useRef(null)
    const headerRef = useRef(null)
    const overlineRef = useRef(null)
    const manifestoRef = useRef(null)
    const bodyRef = useRef(null)
    const ctaRef = useRef(null)
    const rightRef = useRef(null)
    const marqueeRef = useRef(null)

    const words = buildManifestoWords()

    useEffect(() => {
        const ctx = gsap.context(() => {

            const trigger = el => ({
                trigger: el,
                start: 'top 88%',
                toggleActions: 'play none none none',
            })

            // Header rule row
            gsap.fromTo(headerRef.current,
                { opacity: 0, y: -12 },
                { opacity: 1, y: 0, duration: 0.8, ease: 'expo.out', scrollTrigger: trigger(headerRef.current) }
            )

            // Overline
            gsap.fromTo(overlineRef.current,
                { opacity: 0, y: 16 },
                { opacity: 1, y: 0, duration: 0.6, ease: 'expo.out', scrollTrigger: trigger(overlineRef.current) }
            )

            // Manifesto — word by word stagger
            const wordEls = manifestoRef.current.querySelectorAll('.ds-word')
            gsap.fromTo(wordEls,
                { opacity: 0, y: 28 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.6,
                    ease: 'expo.out',
                    stagger: 0.055,
                    scrollTrigger: trigger(manifestoRef.current),
                }
            )

            // Body paragraph
            gsap.fromTo(bodyRef.current,
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, duration: 0.7, ease: 'expo.out', delay: 0.2, scrollTrigger: trigger(bodyRef.current) }
            )

            // CTA row
            gsap.fromTo(ctaRef.current,
                { opacity: 0, y: 16 },
                { opacity: 1, y: 0, duration: 0.6, ease: 'expo.out', delay: 0.1, scrollTrigger: trigger(ctaRef.current) }
            )

            // Right principles panel
            gsap.fromTo(rightRef.current,
                { opacity: 0, y: 32 },
                { opacity: 1, y: 0, duration: 0.9, ease: 'expo.out', scrollTrigger: trigger(rightRef.current) }
            )

            // Marquee fade-in
            gsap.fromTo(marqueeRef.current,
                { opacity: 0 },
                { opacity: 1, duration: 0.6, ease: 'power2.out', scrollTrigger: trigger(marqueeRef.current) }
            )

        }, wrapRef)

        return () => ctx.revert()
    }, [])

    return (
        <section ref={wrapRef} className="ds-wrap">

            {/* Texture layers */}
            <div className="ds-noise" />
            <div className="ds-bg-grid" />
            <div className="ds-ghost-word">Origin</div>

            <div className="ds-inner">

                {/* ── Header rule ── */}
                <div ref={headerRef} className="ds-header-row">
                    <div className="ds-header-line ds-header-line--left" />
                    <div className="ds-header-tag">
                        <span className="ds-tag-diamond" />
                        AVIATRICKS — DESIGN PHILOSOPHY
                        <span className="ds-tag-diamond" />
                    </div>
                    <div className="ds-header-line ds-header-line--right" />
                </div>

                {/* ── Main grid ── */}
                <div className="ds-main-grid">

                    {/* Left */}
                    <div className="ds-left">

                        <div ref={overlineRef} className="ds-overline">
                            <div className="ds-overline-rule" />
                            <span className="ds-overline-text">THE MANIFESTO</span>
                        </div>

                        {/* Manifesto — words wrapped for GSAP stagger */}
                        <p ref={manifestoRef} className="ds-manifesto">
                            {words.map((item, i) =>
                                item.em
                                    ? <em key={i}><span className="ds-word">{item.word}</span></em>
                                    : <span key={i} className="ds-word">{item.word}</span>
                            )}
                        </p>

                        <p ref={bodyRef} className="ds-body-text">
                            India has no shortage of missions that demand precision.
                            What it lacked was a manufacturer willing to build the
                            machine <strong>from the ground up</strong> — geometry,
                            composite lay-up, payload integration — without reaching
                            for an imported catalogue.
                            <br /><br />
                            Aviatricks was founded on a single refusal:{' '}
                            <strong>to never compromise the frame for the sake of convenience.</strong>
                        </p>

                        <div ref={ctaRef} className="ds-cta-row">
                            <a href="/contact/support" className="ds-cta-btn">
                                <span>DISCUSS YOUR MISSION</span>
                                <span>→</span>
                            </a>
                            {/* <a href="/process" className="ds-cta-ghost-link">
                                SEE HOW WE BUILD →
                            </a> */}
                        </div>

                    </div>

                    {/* Right — principles */}
                    <div ref={rightRef} className="ds-right">

                        <div className="ds-principles-label">ENGINEERING PRINCIPLES</div>

                        {PRINCIPLES.map((p, i) => (
                            <div key={i} className="ds-principle">
                                <div className="ds-principle-sweep" />
                                <div className="ds-principle-num">{p.num}</div>
                                <div className="ds-principle-title">
                                    {p.title.split('\n').map((line, j) => (
                                        <span key={j}>{line}{j === 0 && <br />}</span>
                                    ))}
                                </div>
                                <div className="ds-principle-body">{p.body}</div>
                            </div>
                        ))}

                    </div>
                </div>
            </div>

            {/* ── Marquee ── */}
            <div ref={marqueeRef} className="ds-marquee-outer">
                <div className="ds-marquee-track">
                    {[...MARQUEE_TAGS, ...MARQUEE_TAGS].map((tag, i) => (
                        <span key={i} className="ds-marquee-item">
                            <span className="ds-marquee-sep" />
                            {tag}
                        </span>
                    ))}
                </div>
            </div>

        </section>
    )
}